import { useTranslation } from 'react-i18next';
import { PhoneIcon, MessageSquareIcon, MapPinIcon } from 'lucide-react';
import { legacyFormatPhone } from '@/entities/legacy-order';
import { legacyTimeOrDash, hasArrived, type LegacyRoutePoint } from '../lib/order-detail';
import { LegacyInfoBox, LegacyChip } from './legacy-info-box';

/**
 * The route list — one row per pickup/drop-off, reproducing the old panel's
 * `OrderAddressInfo`.
 *
 * Each row is address + detail chips on the left and four timing stats on the
 * right. Two of those stats only appear once the courier has ARRIVED at the
 * point (the source gates them on `dayjs(arrived_at).isValid()`), which is why
 * an en-route order shows two stats and a completed one shows four.
 */
export function LegacyOrderAddressInfo({ points }: { points: LegacyRoutePoint[] }) {
  const { t } = useTranslation('legacy');

  return (
    <div className="rounded-[20px] bg-[var(--lg-surface)] p-5">
      {points.map((point, index) => {
        const arrived = hasArrived(point);

        // Rendered only when non-empty, in the source's fixed order.
        const details = [
          { label: t('create.door_phone'), value: point.door_phone },
          { label: t('create.entrance'), value: point.entrance },
          { label: t('create.apartment'), value: point.apartment },
          { label: t('create.floor'), value: point.floor },
        ].filter((d) => Boolean(d.value));

        return (
          <div
            key={point.id ?? index}
            className={`flex w-full flex-col items-start gap-2.5 sm:flex-row sm:items-center sm:justify-between ${
              index !== 0 ? 'pt-5' : ''
            } ${index !== points.length - 1 ? 'pb-5' : ''}`}
          >
            {/* Left: address, detail chips, contact chips */}
            <div className="flex min-w-0 flex-col gap-3">
              <span className="text-[16px] font-semibold text-[var(--lg-fg)]">
                {point.address}
              </span>

              {details.length > 0 && (
                <div className="flex flex-col gap-2 sm:flex-row">
                  {details.map((d) => (
                    <LegacyChip key={d.label}>
                      <span className="font-semibold">{d.label}:</span>
                      <span className="font-medium">{d.value}</span>
                    </LegacyChip>
                  ))}
                </div>
              )}

              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <LegacyChip>
                  {/* Only pickups carry the origin marker, as in the source. */}
                  {point.type === 'origin' && (
                    <MapPinIcon className="size-3.5 shrink-0" aria-hidden="true" />
                  )}
                  <span className="font-medium">{point.client?.name}</span>
                </LegacyChip>

                {point.client?.phone && (
                  <LegacyChip as="a" href={`tel:${point.client.phone}`}>
                    <PhoneIcon className="size-3.5 shrink-0" aria-hidden="true" />
                    <span className="font-medium whitespace-nowrap">
                      {legacyFormatPhone(point.client.phone)}
                    </span>
                  </LegacyChip>
                )}

                {point.comment && (
                  // The source showed only an icon with the comment in a
                  // tooltip; render the text inline so it is readable
                  // without hovering (title kept as a fallback).
                  <LegacyChip>
                    <span
                      title={point.comment}
                      className="inline-flex min-w-0 items-center gap-1"
                    >
                      <MessageSquareIcon
                        className="size-3.5 shrink-0"
                        aria-hidden="true"
                      />
                      <span className="font-medium break-words">{point.comment}</span>
                    </span>
                  </LegacyChip>
                )}
              </div>
            </div>

            {/* Right: timing stats. The first two need an arrival. */}
            <div className="grid w-full grid-cols-4 gap-3 min-[601px]:flex min-[601px]:w-auto min-[601px]:shrink-0 min-[601px]:items-center min-[601px]:gap-8">
              {arrived && (
                <>
                  <LegacyInfoBox
                    title={t('single.distance')}
                    info={`${point.closed_point_at_distance ?? 0} ${t('single.meters')}`}
                  />
                  <LegacyInfoBox
                    title={t('single.should_arrive')}
                    info={legacyTimeOrDash(point.initial_eta)}
                  />
                </>
              )}

              <LegacyInfoBox
                title={arrived ? t('single.arrived_at') : t('single.will_arrive')}
                info={legacyTimeOrDash(arrived ? point.arrived_at : point.eta)}
              />
              <LegacyInfoBox
                title={
                  point.type === 'origin'
                    ? t('single.picked_up_at')
                    : t('single.handed_over_at')
                }
                info={legacyTimeOrDash(point.handed_over_at)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
